/**
 * Utility functions for form validation
 */

/**
 * Check if all required fields in a form data object are filled
 * @param formData - The form data object
 * @param requiredFields - Array of required field names
 * @returns boolean - true if all required fields are filled, false otherwise
 */
export const areRequiredFieldsFilled = (
  formData: Record<string, any>, 
  requiredFields: string[]
): boolean => {
  return requiredFields.every(field => {
    const value = formData[field];
    // Check if value exists and is not empty (handles strings, numbers, etc.)
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
};

/**
 * Check if all required fields are filled with additional validation for edit mode
 * @param formData - The form data object
 * @param requiredFields - Array of required field names
 * @param isEditMode - Whether form is in edit mode
 * @param conditionalFields - Fields that are only required in certain conditions
 * @returns boolean - true if all required fields are filled, false otherwise
 */
export const areRequiredFieldsFilledWithConditions = (
  formData: Record<string, any>,
  requiredFields: string[],
  isEditMode: boolean = false,
  conditionalFields: { [key: string]: boolean } = {}
): boolean => {
  // Check base required fields
  const baseFieldsFilled = areRequiredFieldsFilled(formData, requiredFields);
  
  // Check conditional fields
  const conditionalFieldsFilled = Object.entries(conditionalFields).every(([field, isRequired]) => {
    if (!isRequired) return true;
    const value = formData[field];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
  
  return baseFieldsFilled && conditionalFieldsFilled;
};
